import type { Row, Table } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import Checkbox from '../../../checkbox/checkbox';
import { Tooltip } from '../../../tooltip/tooltip';

interface CheckboxCellProps<T> {
  row: Row<T>;
  disabledTooltip?: string | ((row: Row<T>) => string | undefined);
}

export const CheckboxCell = <T,>({
  row,
  disabledTooltip,
}: CheckboxCellProps<T>) => {
  const { t } = useTranslation();
  const isDisabled = !row.getCanSelect();
  const tooltipText =
    typeof disabledTooltip === 'function'
      ? disabledTooltip(row)
      : disabledTooltip;

  const checkbox = (
    <StyledCheckbox
      value={row.getIsSelected()}
      onChange={row.getToggleSelectedHandler()}
      onClick={(event) => event.stopPropagation()} // Prevent calls to onRowClick
      disabled={isDisabled}
      showLabel={false}
      label={t('phrases.selectRow', { rowId: row.id })}
      size="16px"
    />
  );

  if (isDisabled && tooltipText) {
    return <Tooltip tooltipText={tooltipText}>{checkbox}</Tooltip>;
  }

  return checkbox;
};

export const CheckboxHeaderCell = <T,>({ table }: { table: Table<T> }) => {
  const { t } = useTranslation();
  const toggleAll = table.getToggleAllRowsSelectedHandler();
  const isAllSelected = table.getIsAllRowsSelected();
  return (
    <StyledCheckbox
      value={isAllSelected}
      onChange={(value) => toggleAll({ target: { value } })}
      onClick={(event) => event.stopPropagation()} // Prevent calls to onRowClick
      disabled={!table.options.enableRowSelection}
      indeterminate={table.getIsSomeRowsSelected()}
      showLabel={false}
      label={isAllSelected ? t('phrases.deselectAll') : t('phrases.selectAll')}
      size="16px"
    />
  );
};

const StyledCheckbox = styled(Checkbox)`
  ${({ theme }) => css`
    // Use padding and negative margin to increase the hitbox for better UX
    padding: ${theme.spacing(2)};
    margin: ${theme.spacing(-2)};
    cursor: pointer;

    input {
      margin: 0;
    }
  `}
`;
